import { apiConnection } from "@/services/api-connection";
import { notificationManager } from "@/services/utilities/notification-manager";
import { formValidator } from "@/services/form/form-validator";
import router from "@/router";
import { formManager } from "@/services/form/form-manager";
import { questionnaireStore, studeaManagerQuestionnaireStore, triadStore } from "@/main";
import { useQuestionnaireStore } from "@/store/questionnaire/questionnaire";
import { messengerMessageManager } from "@/services/messenger-message/messenger-message-manager";
import { i18n } from "@/i18n";
import { userManager } from "@/services/security/user-manager";

const URL_QUESTIONNAIRES = "/app/questionnaire/list/";
const URL_QUESTIONNAIRE = "/app/questionnaire/";
const URL_POST = "/app/questionnaire/save/";
const URL_PDF = "/app/questionnaire/export/";

class QuestionnaireManager {
  QUESTION_TYPE_TEXT = 0;
  QUESTION_TYPE_TEXTAREA = 1;
  QUESTION_TYPE_SELECT = 2;
  QUESTION_TYPE_RADIO = 3;
  QUESTION_TYPE_CHECKBOX = 4;
  QUESTION_TYPE_INSTITUTION_LIST = 5;
  QUESTION_TYPE_COMPONENT_LIST = 6;
  QUESTION_TYPE_DEGREE_LIST = 7;
  QUESTION_TYPE_NUMBER = 8;
  QUESTION_TYPE_UFA_LIST = 9;
  QUESTION_TYPE_FILE = 10;
  QUESTION_TYPE_DATE = 11;
  QUESTION_TYPE_DATETIME = 12;
  DUPLICATION_TYPE_ADD = 1;
  DUPLICATION_TYPE_REPLACE = 2;
  DUPLICATION_TYPE_OVERWRITE = 3;
  USE_CUSTOM_DATE = 1;
  USE_CREW_DATE = 2;
  USE_CONTRACT_DATE = 3;
  QUESTIONNAIRE_REPLY_STATUS_DRAFTED = 1;
  QUESTIONNAIRE_REPLY_STATUS_SHARED = 2;
  QUESTIONNAIRE_REPLY_STATUS_SIGNED = 3;
  _signingInProgress = false;

  postAdminQuestionnaire(data, trainingCourseId, id = null) {
    const url = "/app/studea-manager/" + trainingCourseId + "/questionnaire";
    return (id ? apiConnection.put(url + "/" + id, data) : apiConnection.post(url, data)).then((response) => response);
  }

  getAdminQuestionnaireList(trainingCourseId) {
    const url = "/app/studea-manager/" + trainingCourseId + "/questionnaires";
    return apiConnection.get(url, {}).then((response) => response);
  }

  getAdminQuestionnaire(id, trainingCourseId, exp = false) {
    const url = "/app/studea-manager/" + trainingCourseId + "/questionnaire/" + (exp ? "export/" : "") + id;
    return apiConnection.get(url, {}).then((response) => response);
  }

  loadQuestionnaires = (id) => {
    return apiConnection.get(URL_QUESTIONNAIRES + id, {}, true).then((response) => response);
  };

  loadQuestionnaire = (questionnaireId, triadId) => {
    return apiConnection
      .get(URL_QUESTIONNAIRE + triadId + "/" + questionnaireId, {}, true)
      .then((response) => response);
  };

  getPdf = (triadId, questionnaireId, model = 0) => {
    return apiConnection.get(URL_PDF + triadId + "/" + questionnaireId, { model: model }).then((res) => {
      messengerMessageManager.processMessage(res);
    });
  };

  postQuestionnaire = (questionnaireId, triadId, draft, model, profile) => {
    if (!model) {
      model = [];
    }
    return apiConnection
      .put(
        URL_POST + triadId + "/" + questionnaireId,
        {
          draft: draft,
          model: model,
          profile: profile,
        },
        true,
      )
      .then((response) => response);
  };

  getFieldBlockPrefixes(type) {
    let block_prefixes = [];
    switch (type) {
      case 0:
        block_prefixes = ["", "text"];
        break;
      case 1:
        block_prefixes = ["", "text", "textarea", "zama_tiny_mce_type"];
        break;
      case 2:
      case 3:
      case 4:
        block_prefixes = ["", "choice"];
        break;
      case 8:
        block_prefixes = ["", "number"];
        break;
      case 10:
        block_prefixes = ["", "file"];
        break;
      case 11:
        block_prefixes = ["", "date"];
        break;
      case 12:
        block_prefixes = ["", "date", "datetime"];
        break;
    }

    return block_prefixes;
  }

  buildModel(questionnaire) {
    let model = {};
    if (questionnaire && questionnaire.questions && Object.keys(questionnaire.questions).length) {
      Object.values(questionnaire.questions).forEach((question) => {
        if (question.itemType === "field") {
          model[question.id] = this.buildField(questionnaire, question);
        } else if (question.itemType === "cluster" && question.displayMode === 2) {
          if (question.questions && Object.keys(question.questions).length) {
            Object.values(question.questions).forEach((row) => {
              row["cluster"] = question.id;
              const name =
                question.userAnswer && question.userAnswer.arrayValue && question.userAnswer.arrayValue.name
                  ? question.userAnswer.arrayValue.name
                  : null;
              const comments =
                row.userAnswer && row.userAnswer.arrayValue && row.userAnswer.arrayValue.comments
                  ? row.userAnswer.arrayValue.comments
                  : null;
              let values =
                row.userAnswer && row.userAnswer.arrayValue && row.userAnswer.arrayValue.answers
                  ? row.userAnswer.arrayValue.answers
                  : null;

              if (name) {
                row["name"] = name;
              }
              model[row.id] = this.buildField(questionnaire, row, question.type);
              model[row.id].vars["row"] = true;

              if (comments) {
                model[row.id].vars["comments"] = comments;
              }

              if (!values) {
                values = [];
                if (question.valueList) {
                  for (let i = 0; i < question.valueList.length; i++) {
                    values[i] = "";
                  }
                }
              }
              model[row.id].vars.value = values;
            });
          }
          if (question.openedQuestions) {
            if (question.userAnswerCollections && Object.keys(question.userAnswerCollections).length) {
              Object.values(question.userAnswerCollections).forEach((userAnswerCollection) => {
                if (Object.keys(userAnswerCollection.answersValues).length) {
                  Object.values(userAnswerCollection.answersValues).forEach((row) => {
                    this.addClusterLine(row, question);
                    model[row.id] = this.buildField(questionnaire, row, question.type, question);
                    model[row.id]["cluster"] = question.id;
                    if (row.arrayValue) {
                      model[row.id]["name"] = row.arrayValue.name ? row.arrayValue.name : null;
                      if (row.arrayValue.comments) {
                        model[row.id].vars["comments"] = row.arrayValue.comments;
                      }
                      row["answer"] = row.arrayValue.answers;
                      model[row.id].vars.value = row.answer;
                    }
                  });
                }
              });
            }
          }
        }
      });
    }

    return model;
  }

  buildField(questionnaire, question, type = null, cluster = null, value = null) {
    let comments;
    type = type ? type : question.type;
    if (question.userAnswer && question.userAnswer.arrayValue && question.userAnswer.arrayValue.answers && !value) {
      value = this.isValidAnswerForFieldType(type, question.userAnswer.arrayValue.answers)
        ? question.userAnswer.arrayValue.answers
        : null;
      comments =
        typeof question.userAnswer.arrayValue.comments === "object"
          ? question.userAnswer.arrayValue.comments.value
          : question.userAnswer.arrayValue.comments;
    }

    const disabled = this.disabled(question);

    const field = {
      vars: {
        value: value,
        block_prefixes: this.getFieldBlockPrefixes(type),
        required: question.required,
        id: question.id,
        name: "name-" + question.id,
        label: question.title,
        help: this.help(question),
        disabled: disabled,
        ignoreValidation: disabled,
        hide: !question.enabled || (questionnaire && questionnaire.userHasSigned),
        attr: {},
        comments: comments,
      },
    };

    if (question.type === this.QUESTION_TYPE_FILE || type === 1) {
      field.vars.max_file_size = 10;
      field.vars["conf_name"] = "triad";
      field.vars["conf_path"] = "questionnaire";
      field.vars["extensions_accepted_message"] =
        "Merci de sélectionner un fichier image uniquement (webp, jpg, gif, png)";
      field.vars["extensions_accepted"] = "(\\.|\\/)(gif|jpe?g|png|pdf|doc|docx|xlsx|xls)$";
    }

    if (this.isChoice(type)) {
      switch (type) {
        case this.QUESTION_TYPE_SELECT:
          field.vars["multiple"] = false;
          field.vars["expanded"] = false;
          break;
        case this.QUESTION_TYPE_RADIO:
          field.vars["multiple"] = false;
          field.vars["expanded"] = true;
          break;
        case this.QUESTION_TYPE_CHECKBOX:
          field.vars["multiple"] = true;
          field.vars["expanded"] = true;
          break;
      }

      let choices = [];
      (cluster ? cluster : question).valueList.forEach((choice) => {
        let isChecked = false;

        // Determine if this choice is checked based on the current value
        if (value) {
          if (Array.isArray(value)) {
            // For checkbox/radio with array values
            isChecked = !!value.find((item) => {
              return (typeof item === "object" && item.value === choice.value) || item === choice.value;
            });
          } else if (typeof value === "object") {
            // For object values, check if any value matches
            isChecked = !!Object.values(value).find((item) => item === choice.value);
          } else if (typeof value === "string") {
            // For string values (select), check if it matches exactly
            isChecked = value === choice.value;
          }
        }

        choices.push({
          label: choice.value,
          value: choice.value,
          data: choice.value,
          mark: choice.mark ? choice.mark : null,
          checked: isChecked,
        });
      });
      field.vars.choices = choices;
      if (this.isCheckbox(field)) {
        field.vars.value = choices;
      }
    }

    return field;
  }

  isValidAnswerForFieldType(type, answer) {
    const isDate = /^\d{4}-\d{2}-\d{2}$/.test(answer);
    const isDateTime = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(answer);

    if ([0, 1, 3, 10].includes(type) && !isDate && !isDateTime) return typeof answer === "string";
    if (type === 2) return typeof answer === "string";
    if (type === 4) return Array.isArray(answer);
    if (type === 8) return typeof answer === "number";
    if (type === 11) return isDate;
    if (type === 12) return isDateTime;
    return false;
  }

  addClusterLine(answer, cluster) {
    if (!cluster.questions) {
      cluster.questions = [];
    }

    const exist = Object.values(cluster.questions).find((item) => item.id === answer.id);

    if (!exist) {
      studeaManagerQuestionnaireStore.initLastIndex(this.lastIndex++);

      const item = {
        id: answer.id,
        index: studeaManagerQuestionnaireStore.lastIndex,
        title: answer.arrayValue && answer.arrayValue.name ? answer.arrayValue.name : "",
        type: cluster.type,
        clusterType: "field",
        access: cluster.openedQuestionsAccess,
        comments: cluster.comments,
        commentsLabel: cluster.commentsLabel,
        commentsType: cluster.commentsType,
        openedQuestionsAccess: [],
        openedQuestions: false,
        openedQuestionsType: 1,
        enabled: this.isEnabled(cluster),
      };

      cluster.questions.push(item);
    }
  }

  isEnabled(question) {
    const questionnaire = useQuestionnaireStore().questionnaire;
    return (
      question.openedQuestionsAccess.includes(questionnaire.userProfile) &&
      questionnaire.userHasToComplete &&
      !questionnaire.hasSigned
    );
  }

  isChoice(type) {
    return [2, 3, 4].includes(type);
  }

  help(question) {
    return question.tipActive && question.tip ? question.tip : null;
  }

  disabled(question) {
    let available =
      questionnaireStore.questionnaire.userHasToComplete &&
      !questionnaireStore.questionnaire.userHasSigned &&
      question.enabled;

    return !available;
  }

  isCheckbox(question) {
    return formManager.isCheckboxType(question);
  }

  saveQuestionnaire(replyStatus, message, error, triadId, questionnaireId) {
    if (replyStatus === this.QUESTIONNAIRE_REPLY_STATUS_DRAFTED && this._signingInProgress) {
      return Promise.resolve(null);
    }
    if (replyStatus >= this.QUESTIONNAIRE_REPLY_STATUS_SHARED) {
      this._signingInProgress = true;
    }

    let validation = [];
    if (replyStatus === this.QUESTIONNAIRE_REPLY_STATUS_SIGNED) {
      validation = formValidator.validForm(questionnaireStore.model, true, true);
    }

    return new Promise((resolve) => {
      if ((validation.errors && !validation.errors.length) || replyStatus !== 3) {
        const modelToSave = {};

        Object.values(questionnaireStore.model).forEach((question) => {
          modelToSave[question.vars.id] = {};
          let value;
          if (this.isCheckbox(question)) {
            value = [];
            Object.values(question.vars.value).forEach((choice) => {
              if (typeof choice !== "string" && choice && choice.checked) {
                value.push(choice.value);
              } else if (typeof choice === "string") {
                value.push(choice);
              }
            });
          } else {
            value = question.vars.value;
          }

          modelToSave[question.vars.id]["answers"] = value;

          if (question.vars.answerComment) {
            modelToSave[question.vars.id]["answerComment"] = question.vars.answerComment;
          }
          if (question.vars.answerId) {
            modelToSave[question.vars.id]["answerId"] = question.vars.answerId;
          }
          if (question.name) {
            modelToSave[question.vars.id]["name"] = question.name;
          }
          if (question.vars.comments) {
            modelToSave[question.vars.id]["comments"] = question.vars.comments;
          }
          if (question.cluster) {
            modelToSave[question.vars.id]["cluster"] = question.cluster;
          }
          if (question.delete) {
            modelToSave[question.vars.id]["delete"] = true;
          }
        });

        let profile = questionnaireStore.questionnaire.userProfile;

        if (!profile && triadStore.triad.userRoles && triadStore.triad.userRoles[0]) {
          profile = triadStore.triad.userRoles[0];
        }

        this.postQuestionnaire(questionnaireId, triadId, replyStatus, modelToSave, profile).then((success) => {
          this._signingInProgress = false;
          if (success && !success.error) {
            Object.keys(modelToSave).forEach((id) => {
              if (id.includes("new-")) {
                delete modelToSave[id];
              }
            });
            if (replyStatus !== this.QUESTIONNAIRE_REPLY_STATUS_DRAFTED) {
              notificationManager.showNotification("success", message);
              const magicLink = apiConnection.getMagicLinkToken();
              if (!magicLink) {
                questionnaireStore.fetchQuestionnaires(triadId);
                router.push({ name: "Dashboard" });
              } else {
                questionnaireStore.fetchQuestionnaire({
                  refresh: true,
                  questionnaire: questionnaireId,
                  triad: triadId,
                });
              }
            } else {
              questionnaireStore.updateQuestionnaireStatus(1);
              notificationManager.showNotification("success", message);
            }
            resolve(success);
          }
        });
      } else {
        this._signingInProgress = false;
        resolve(validation.errors);
      }
    });
  }

  addRow(triadId, questionnaireId, clusterId) {
    return apiConnection
      .post("/app/questionnaire/add-row/" + triadId + "/" + questionnaireId + "/" + clusterId)
      .then((answer) => answer);
  }

  copyQuestionnaire(triadId, questionnaireId, params) {
    return apiConnection
      .post("/app/questionnaire/copy-questionnaire/" + triadId + "/" + questionnaireId, params)
      .then((response) => response);
  }

  removeQuestionnaire(questionnaireId) {
    return apiConnection
      .delete("/app/questionnaire/remove-questionnaire/" + questionnaireId)
      .then((response) => response);
  }

  addComment(triadId, questionnaireId, answerId, comment) {
    return apiConnection
      .post("/app/questionnaire/add-comment/" + triadId + "/" + questionnaireId + "/" + answerId, {
        comment: comment,
      })
      .then((comment) => comment);
  }

  processQuestionnaire(questionnaire, tabs) {
    const choicesToProcess = [
      "signedBy",
      "visibleBy",
      "marksVisibleBy",
      "interviewDateVisibleBy",
      "canBeCopiedBy",
      "triads",
      "crews",
      "skillSession",
      "secondaryQuestionnaires",
      "signedByStudeaManagerProfiles",
      "versionItemMarksVisibleBy",
    ];
    const fieldsToProcess = [
      "defaultApprenticeAccessStartDate",
      "defaultApprenticeAccessEndDate",
      "defaultTutorAccessStartDate",
      "defaultTutorAccessEndDate",
      "defaultApprenticeMasterAccessStartDate",
      "defaultApprenticeMasterAccessEndDate",
      "defaultStudeaManagerAccessStartDate",
      "defaultStudeaManagerAccessEndDate",
      "completenessDate",
      "firstReminder",
      "secondReminder",
    ];

    const collectionToProcess = ["summarySheetQuestionnaires"];

    choicesToProcess.forEach((field) => {
      let values = [];
      if (questionnaire[field] && Object.keys(questionnaire[field]).length) {
        Object.values(questionnaire[field]).forEach((choice) => {
          if (choice.checked || choice.id) {
            let value = parseInt(choice.id ? choice.id : choice.value);
            if (["triads", "crews", "signedByStudeaManagerProfiles"].includes(field)) {
              value = { id: parseInt(choice.id ? choice.id : choice.value) };
            }
            values.push(value);
          }
          if (["secondaryQuestionnaires"].includes(field)) {
            values.push({ id: parseInt(choice.value) });
          }
        });
      }

      if (["skillSession"].includes(field)) {
        // In some case, skillSession field is not present in formType, so we have to reset skillSession value to null to avoid to pass an object to the post skillSession data
        if (!questionnaire[field]) {
          values = null;
        } else {
          values = questionnaire[field].data ? questionnaire[field].data : questionnaire[field];
        }
      }
      questionnaire[field] = values;
    });

    fieldsToProcess.forEach((field) => {
      if (!questionnaire[field]) {
        questionnaire[field] = null;
      }
    });

    collectionToProcess.forEach((field) => {
      if (tabs) {
        let targetTab = Object.values(tabs).find((item) => item.code === "questionnaireSummarySheet").models;
        if (
          targetTab.summarySheetQuestionnaires.children &&
          targetTab.summarySheetQuestionnaires.children.length &&
          targetTab.summarySheetQuestionnaires.children[0].children.questionnaire.vars.value
        )
          formManager.processCollectionType(targetTab[field], questionnaire);

        if (questionnaire.summarySheetQuestionnaires && questionnaire.summarySheetQuestionnaires.length) {
          questionnaire.summarySheetQuestionnaires.forEach((summarySheet) => {
            summarySheet.summarySheet = { id: questionnaire.id };
          });
        }
      }
    });

    if (questionnaire.questions && Object.keys(questionnaire.questions).length) {
      Object.values(questionnaire.questions).forEach((question) => {
        delete question["selected"];
        delete question["index"];

        if (question.questions && Object.keys(question.questions).length) {
          Object.values(question.questions).forEach((subQuestion) => {
            delete subQuestion["selected"];
            delete subQuestion["index"];
          });
        }
      });
    }

    return questionnaire;
  }

  updateFields(models, checkDates = false) {
    models = Object.values(models);
    const visibleBy = models.find((item) => item.vars.name === "visibleBy");

    const visibleByApprentice = visibleBy ? visibleBy.vars.value.find((item) => item.data === 1) : null;
    const visibleByTutor = visibleBy ? visibleBy.vars.value.find((item) => item.data === 2) : null;
    const visibleByApprenticeMaster = visibleBy ? visibleBy.vars.value.find((item) => item.data === 3) : null;

    const signedBy = models.find((item) => item.vars.name === "signedBy");
    const signedByApprentice = signedBy ? signedBy.vars.value.find((item) => item.data === 1) : null;

    const signedByTutor = signedBy ? signedBy.vars.value.find((item) => item.data === 2) : null;

    const signedByApprenticeMaster = signedBy ? signedBy.vars.value.find((item) => item.data === 3) : null;

    if (checkDates) {
      const defaultDate = models.find((item) => item.vars.name === "useSameDates");
      this.checkDates(models, signedBy, defaultDate && defaultDate.vars.value);
    }

    if (signedByApprentice) {
      signedByApprentice.disabled = !visibleByApprentice || !visibleByApprentice.checked;
      signedByApprentice.checked = signedByApprentice.disabled ? false : signedByApprentice.checked;
    }

    if (signedByTutor) {
      signedByTutor.disabled = !visibleByTutor || !visibleByTutor.checked;
      signedByTutor.checked = signedByTutor.disabled ? false : signedByTutor.checked;
    }

    if (signedByApprenticeMaster) {
      signedByApprenticeMaster.disabled = !visibleByApprenticeMaster || !visibleByApprenticeMaster.checked;
      signedByApprenticeMaster.checked = signedByApprenticeMaster.disabled ? false : signedByApprenticeMaster.checked;
    }

    const incidentSheet = models.find((item) => item.vars.name === "incidentSheet");

    const canBeCopiedBy = models.find((item) => item.vars.name === "canBeCopiedBy");

    if (canBeCopiedBy) {
      canBeCopiedBy.vars.required = incidentSheet && incidentSheet.vars.value;
    }
  }

  checkDates(models, signedBy, useDefault = false, questionnaire = null) {
    const startDates = [
      { field: "defaultApprenticeAccessStartDate", access: userManager.PROFILE_APPRENTICE },
      { field: "defaultTutorAccessStartDate", access: userManager.PROFILE_TUTOR },
      { field: "defaultApprenticeMasterAccessStartDate", access: userManager.PROFILE_APPRENTICE_MASTER },
      { field: "defaultStudeaManagerAccessStartDate", access: userManager.PROFILE_STUDEA_MANAGER },
    ];

    const endDates = [
      { field: "defaultApprenticeAccessEndDate", access: userManager.PROFILE_APPRENTICE },
      { field: "defaultTutorAccessEndDate", access: userManager.PROFILE_TUTOR },
      { field: "defaultApprenticeMasterAccessEndDate", access: userManager.PROFILE_APPRENTICE_MASTER },
      { field: "defaultStudeaManagerAccessEndDate", access: userManager.PROFILE_STUDEA_MANAGER },
    ];

    let defaultStartDate;
    let defaultEndDate;

    if (useDefault) {
      defaultStartDate = Object.values(models).find((item) => item.vars.name === "defaultStartDate");
      defaultEndDate = Object.values(models).find((item) => item.vars.name === "defaultEndDate");
    }

    startDates.forEach((date) => {
      const field = Object.values(models).find((item) => item.vars.name === date.field);
      const signatory = Object.values(signedBy.vars.value).find((choice) => choice.data === date.access);
      field.vars.required = signatory && signatory.checked;

      if (defaultStartDate) {
        field.vars.value = signatory && signatory.checked ? defaultStartDate.vars.value : null;

        if (questionnaire) {
          questionnaire[date.field] = signatory && signatory.checked ? defaultStartDate.vars.value : null;
        }
      }
    });

    endDates.forEach((date) => {
      const field = Object.values(models).find((item) => item.vars.name === date.field);
      const signatory = Object.values(signedBy.vars.value).find((choice) => choice.data === date.access);
      field.vars.required = signatory && signatory.checked;

      if (defaultEndDate) {
        field.vars.value = signatory && signatory.checked ? defaultEndDate.vars.value : null;

        if (questionnaire) {
          questionnaire[date.field] = signatory && signatory.checked ? defaultEndDate.vars.value : null;
        }
      }
    });
  }

  validQuestion(question, valid) {
    const isCluster = question.itemType === "cluster" && question.questions && Object.keys(question.questions).length;
    if (question.itemType === "field") {
      if (question.access.length === 0) {
        question["accessError"] = true;
      } else {
        delete question.accessError;
      }

      if (!question.title) {
        question["titleError"] = true;
      } else {
        delete question.titleError;
      }

      if (valid && (question.accessError || question.titleError)) {
        valid = false;
      }
    } else if (isCluster) {
      if (!question.title) {
        question["titleError"] = true;
      } else {
        delete question.titleError;
      }
      Object.values(question.questions).forEach((row) => {
        if (row.access.length === 0) {
          row["accessError"] = true;
          row.selected = question.selected;
        } else {
          delete row.accessError;
        }

        if (!row.title) {
          row["titleError"] = true;
        } else {
          delete row.titleError;
        }

        if (valid && (row.accessError || row.titleError)) {
          valid = false;
        }
      });
      Object.values(question.valueList).forEach((value) => {
        if (!value.value.length) {
          value["error"] = i18n.global.t("studea_manager.questionnaire.value_title_missing");
        } else {
          delete value.error;
        }

        if (valid && value.error) {
          valid = false;
        }
      });
    }
    return valid;
  }

  toggleQuestionnaireActive(trainigCourseId, id) {
    return apiConnection
      .get("/app/studea-manager/" + trainigCourseId + "/questionnaire/toggle-active/" + id)
      .then((res) => res);
  }

  getColumnMaxWidth(column) {
    if (column.value && column.value.length) {
      return 40 + 4 * column.value.length;
    } else {
      return 60;
    }
  }
}

export const questionnaireManager = new QuestionnaireManager();
